import {
  cloneElement,
  useState,
  useMemo,
  Fragment,
  Dispatch,
  SetStateAction,
} from 'react';
import { createPortal } from 'react-dom';
import { nanoid } from 'nanoid';

import { useContextualSurface } from 'approot/shared/ally/contextual.hooks';
import {
  useDropdownTrap,
  useDropdownTrigger,
} from 'approot/shared/ally/keyboard.hooks';
import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { Props } from './dropdown.constants';
import {
  DropdownDivider,
  DropdownItem,
  DropdownItemDescription,
  DropdownItemLabel,
} from './dropdown-item.style';
import * as Styled from './dropdown.style';

export const Dropdown = ({
  options,
  buttonElement,
  borderRadius,
  dropdownButtonContents,
  dropdownContents,
  disabled,
  selectedValue,
  placement,
  offset,
  fallbackPlacements,
  isLoading,
  isSmall,
  closeMenuOnSelect = true,
  menuWidth,
  menuHeight,
  menuContainerElement,
  menuMatchToggleWidth,
  tooltip,
  onSelect,
  onToggle,
  onClose,
  onMenuItemSelect,
  isInline,
  hasError,
  strategy,
  footer,
  shouldPreventEventPropagation,
  ...rest
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const {
    styles,
    attributes,
    referenceElement,
    setReferenceElement,
    popperElement,
    setPopperElement,
  } = useContextualSurface(
    isActive,
    setIsActive,
    {
      placement: placement || 'bottom-start',
      fallbackPlacements,
      strategy,
      offset,
    },
    shouldPreventEventPropagation
  );

  const id = useMemo(() => nanoid(), []);
  const trigger = referenceElement as HTMLButtonElement | null;

  useDropdownTrap(popperElement, trigger, () => {
    setIsActive(false);
    onClose?.();
  });
  useDropdownTrigger(trigger);

  const toggleProps: {
    type: 'button';
    'aria-haspopup': 'listbox';
    'aria-expanded': boolean;
    'aria-label'?: string;
    'data-dropdown-container-id': string;
    disabled?: boolean;
    $borderRadius?: number;
    className: string;
    ref: Dispatch<SetStateAction<HTMLElement | null>>;
    onClick: () => void;
    isInline?: boolean;
  } = {
    type: 'button',
    'aria-haspopup': 'listbox',
    'aria-expanded': isActive,
    'aria-label': rest['aria-label'],
    'data-dropdown-container-id': id,
    disabled: !!disabled,
    className: isActive ? 'is-active' : '',
    $borderRadius: borderRadius,
    ref: setReferenceElement,
    onClick: () => {
      setIsActive(!isActive);
      onToggle?.(!isActive);
    },
    isInline,
    ...rest,
  };

  if (isLoading) {
    return <Styled.DropdownSkeleton {...rest} />;
  }

  const toggle = dropdownButtonContents ? (
    cloneElement(dropdownButtonContents, {
      ...toggleProps,
    })
  ) : (
    <Styled.DropdownToggle isTransparent={false} {...toggleProps}>
      <Styled.DropdownToggleContent data-selector="dropdown-button-text">
        {buttonElement
          ? buttonElement
          : selectedValue
          ? options?.find(o => o.value === selectedValue)?.label ||
            selectedValue
          : 'Please select'}
      </Styled.DropdownToggleContent>
      <DropdownIndicator />
    </Styled.DropdownToggle>
  );

  const menuContainer = menuContainerElement
    ? document.getElementById(menuContainerElement)
    : null;

  const renderMenu = () => (
    <>
      <Styled.DropdownMenu
        data-selector="dropdown-menu"
        id={id}
        ref={setPopperElement}
        role="listbox"
        style={styles.popper}
        tabIndex={-1}
        menuWidth={menuWidth}
        menuHeight={menuHeight}
        onClick={() => {
          if (closeMenuOnSelect) {
            setIsActive(false);
            onClose?.();
          }
          // this allows further control of the dropdown
          onMenuItemSelect?.({ closeDropdown: () => setIsActive(false) });
        }}
        {...attributes.popper}
      >
        {dropdownContents
          ? dropdownContents
          : options?.map((option, idx) =>
              option.component ? (
                <Fragment key={`dropdown-${idx}`}>{option.component}</Fragment>
              ) : option.isDivider ? (
                <DropdownDivider key={`dropdown-${idx}`} />
              ) : (
                <DropdownItem
                  data-selector="dropdown-options"
                  role="option"
                  key={option.value}
                  disabled={option.isDisabled}
                  $isNegative={option.isNegative}
                  $isActive={option.value === selectedValue}
                  aria-selected={option.value === selectedValue}
                  onClick={() => onSelect?.(option)}
                >
                  {option.description ? (
                    <>
                      <DropdownItemLabel>{option.label}</DropdownItemLabel>
                      <DropdownItemDescription>
                        {option.description}
                      </DropdownItemDescription>
                    </>
                  ) : (
                    option.label
                  )}
                </DropdownItem>
              )
            )}
      </Styled.DropdownMenu>
      {footer}
    </>
  );

  return (
    <Styled.Dropdown
      isSmall={isSmall}
      hasError={hasError}
      menuMatchToggleWidth={menuMatchToggleWidth}
    >
      {tooltip ? (
        <TooltipWrapper hoverText={tooltip}>{toggle}</TooltipWrapper>
      ) : (
        toggle
      )}
      {isActive &&
        (menuContainer
          ? createPortal(renderMenu(), menuContainer)
          : renderMenu())}
    </Styled.Dropdown>
  );
};

export const DropdownIndicator = () => (
  <Styled.DropdownToggleIcon>
    <InlineSvg id="icon_dropdown" width={10} height={16} />
  </Styled.DropdownToggleIcon>
);

export const DropdownSecondaryMore = ({
  label,
  ...rest
}: Props & {
  label: string;
}) => (
  <Dropdown
    dropdownButtonContents={
      <Styled.DropdownToggleSecondaryMore>
        {label} <InlineSvg id="icon_more" width={10} height={16} />
      </Styled.DropdownToggleSecondaryMore>
    }
    {...rest}
  />
);

export const DropdownIcon = (props: Props) => (
  <Dropdown
    {...props}
    dropdownButtonContents={
      <Styled.DropdownToggle $isIcon>
        {props.buttonElement}
        <DropdownIndicator />
      </Styled.DropdownToggle>
    }
  />
);
